import { useEffect, useState, type FormEvent } from "react";
import { hieraApi, type Me } from "../lib/api";
import { logout } from "../lib/auth";
import { t, type Lang } from "../lib/i18n";
import { EmojiPicker } from "../shell/EmojiPicker";

type Session = { id: string; device: string; lastSeen: string; current: boolean };

type Props = { lang: Lang; me: Me; onMe: (me: Me) => void };

function when(iso: string, lang: Lang): string {
  const then = new Date(iso).getTime();
  if (Date.now() - then < 2 * 60 * 1000) return t(lang, "online");
  return new Date(iso).toLocaleString(lang === "ru" ? "ru-RU" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ProfilePage({ lang, me, onMe }: Props) {
  const [name, setName] = useState(me.displayName);
  const [nick, setNick] = useState(me.username || "");
  const [email, setEmail] = useState(me.email || "");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [key, setKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [picker, setPicker] = useState<{ top: number; left: number } | null>(null);
  const photo = me.avatarUrl || "";

  useEffect(() => {
    void hieraApi.sessions().then((res) => setSessions(res.sessions));
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    const next = await hieraApi.patchMe({
      displayName: name.trim(),
      username: nick.trim().replace(/^@+/, ""),
      email: email.trim(),
    });
    onMe(next);
    setNick(next.username || "");
  }

  async function pickEmoji(emoji: string) {
    setPicker(null);
    const next = await hieraApi.patchMe({ avatarEmoji: emoji });
    onMe(next);
  }

  async function makeKey() {
    if ((me.hasKey || key) && !window.confirm(t(lang, "keyWarn"))) return;
    const issued = await hieraApi.issueKey();
    setKey(issued.key);
    setCopied(false);
    onMe({ ...me, hasKey: true });
  }

  async function dropOthers() {
    await hieraApi.revokeOtherSessions();
    const res = await hieraApi.sessions();
    setSessions(res.sessions);
  }

  async function dropOne(id: string) {
    await hieraApi.revokeSession(id);
    setSessions((rows) => rows.filter((row) => row.id !== id));
  }

  return (
    <div className="account pad">
      <h1>{t(lang, "myProfile")}</h1>
      <div className="account-head">
        {photo ? (
          <img className="account-avatar" src={photo} alt="" width={64} height={64} />
        ) : (
          <button
            type="button"
            className="account-avatar"
            aria-label={t(lang, "avatar")}
            onClick={(e) => {
              const box = e.currentTarget.getBoundingClientRect();
              setPicker({ top: box.bottom + 6, left: box.left });
            }}
          >
            {me.avatarEmoji || name.slice(0, 2).toUpperCase()}
          </button>
        )}
      </div>
      {picker ? (
        <EmojiPicker lang={lang} top={picker.top} left={picker.left} onPick={(emoji) => void pickEmoji(emoji)} onClose={() => setPicker(null)} />
      ) : null}

      <form className="account-form" onSubmit={(e) => void save(e)}>
        <label>
          {t(lang, "name")}
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
        </label>
        <label>
          {t(lang, "username")}
          <input
            value={nick}
            onChange={(e) => setNick(e.target.value.replace(/^@+/, ""))}
            maxLength={64}
            autoComplete="username"
            placeholder="@nick"
          />
        </label>
        <label>
          {t(lang, "email")}
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" maxLength={200} />
        </label>
        {me.heronSub ? (
          <label>
            {t(lang, "heronId")}
            <input value={me.heronSub} readOnly />
          </label>
        ) : null}
        <button type="submit" className="btn-gold">{t(lang, "save")}</button>
      </form>

      <section className="account-block">
        <h2>{t(lang, "serviceKey")}</h2>
        <p className="muted">{t(lang, "serviceKeyHint")}</p>
        {key ? (
          <div className="key-box">
            <code>{key}</code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(key).then(() => setCopied(true));
              }}
            >
              {copied ? t(lang, "copied") : t(lang, "copy")}
            </button>
          </div>
        ) : null}
        <button type="button" className="btn-line" onClick={() => void makeKey()}>
          {me.hasKey || key ? t(lang, "refreshKey") : t(lang, "issueKey")}
        </button>
      </section>

      <section className="account-block">
        <div className="account-split">
          <button
            type="button"
            className="btn-line"
            onClick={() => {
              void logout().then(() => window.location.reload());
            }}
          >
            {t(lang, "logout")}
          </button>
          <button type="button" className="btn-danger" onClick={() => void dropOthers()}>
            {t(lang, "logoutOthers")}
          </button>
        </div>
        <p className="muted">{t(lang, "logoutOthersHint")}</p>
        <h2>{t(lang, "sessions")}</h2>
        <ul className="session-list">
          {sessions.map((row) => (
            <li key={row.id}>
              <span>
                <strong>{row.device}</strong>
                <span className="meta">{row.current ? t(lang, "thisDevice") : when(row.lastSeen, lang)}</span>
              </span>
              {row.current ? null : (
                <button type="button" className="btn-line" onClick={() => void dropOne(row.id)}>
                  {t(lang, "logout")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
